import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { supabase } from '@/integrations/supabase/client';
import { clearDiffsCache } from '@/store/diffsSlice';
import type { RootState } from '@/store/store';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertTriangle,
  Calendar,
  Image as ImageIcon,
  Sparkles,
  Edit3,
  Undo2,
  ExternalLink,
  X,
  Tag,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';
import { AnnotationEditor } from '@/components/diffs/AnnotationEditor';

// Types
interface AnnotationCoordinate {
  x: number;
  y: number;
}

interface ViewportState {
  zoom: number;
  panX: number;
  panY: number;
}

interface Annotation {
  num: number;
  label: string;
  parent_label?: string;
  image?: string;
  before: AnnotationCoordinate | null;
  after: AnnotationCoordinate | null;
  marker_before?: AnnotationCoordinate;  // Explicit marker position (overrides before)
  marker_after?: AnnotationCoordinate;   // Explicit marker position (overrides after)
  viewport_before?: ViewportState;
  viewport_after?: ViewportState;
}

interface AnnotationCoordinates {
  cloudinary_before?: string;
  cloudinary_after?: string;
  annotations: Annotation[];
}

interface DiffEvent {
  id: string;
  event_type: string;
  category: string;
  description: string;
  slug: string;
  v_before: string;
  v_after: string;
  verified: boolean;
  invalid: boolean;
  partially_correct: boolean;
  asset_id: string | null;
  labels: string[];
  tags: string[];
  annotation_coordinates: AnnotationCoordinates | null;
  primary_cropped_url: string | null;
  secondary_cropped_url: string | null;
  primary_original_url: string | null;
  secondary_original_url: string | null;
  created_at: string;
}

interface DiffData {
  news_id: string;
  slug: string;
  v_before: string;
  v_after: string;
  hook: string;
  news_description: string;
  published: boolean;
  period: string;
  delta: number;
  logo_url: string;
  page_id: string;
  events: DiffEvent[];
}

// Category priority for sorting
const categoryPriority: Record<string, number> = {
  'pricing_structure': 1,
  'feature': 2,
  'limits': 3,
  'misc': 4,
};

// Predefined event tags grouped by category
const TAG_CATEGORIES: Record<string, string[]> = {
  'Pricing': ['price_increase', 'price_decrease', 'pricing_restructure', 'usage_based_pricing', 'seat_based_pricing'],
  'Plans': ['new_plan', 'plan_removed', 'plan_renamed', 'plan_consolidated', 'new_addon'],
  'Discounts': ['discount_added', 'discount_removed', 'annual_discount_change', 'volume_discount'],
  'Trial & Free': ['trial_change', 'freemium_change', 'free_tier_added', 'free_tier_removed'],
  'Features': ['feature_added', 'feature_removed', 'premium_tier_movement', 'feature_democratized', 'feature_unbundled'],
  'AI & Tech': ['ai_ml_features', 'automation_features', 'copilot_assistant'],
  'Enterprise': ['enterprise_features', 'security_compliance', 'sso_saml', 'admin_controls'],
  'API & Integrations': ['api_changes', 'integration_added', 'integration_removed', 'developer_tools'],
  'Limits': ['capacity_increase', 'capacity_decrease', 'unlimited_added', 'unlimited_removed', 'storage_change', 'api_rate_change', 'user_limit_change', 'usage_limit_change'],
  'Other': ['page_redesign', 'branding_change', 'clarification', 'currency_region', 'billing_change', 'contract_terms', 'deprecation_notice'],
};

const formatCategoryLabel = (category: string): string => {
  if (!category || category === 'null') return 'Uncategorized';
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Category colors for visual distinction
const getCategoryColors = (category: string): { dot: string; text: string; border: string } => {
  const colorMap: Record<string, { dot: string; text: string; border: string }> = {
    'pricing_structure': { dot: 'bg-purple-500', text: 'text-purple-700', border: 'border-purple-200' },
    'feature': { dot: 'bg-blue-500', text: 'text-blue-700', border: 'border-blue-200' },
    'limits': { dot: 'bg-orange-500', text: 'text-orange-700', border: 'border-orange-200' },
    'misc': { dot: 'bg-gray-500', text: 'text-gray-700', border: 'border-gray-200' },
  };
  return colorMap[category.toLowerCase()] || { dot: 'bg-gray-500', text: 'text-gray-700', border: 'border-gray-200' };
};

const formatVersionDate = (version: string): string => {
  if (!version) return 'Unknown';
  const match = version.match(/(\d{4})(\d{2})(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return format(new Date(`${year}-${month}-${day}`), 'MMM d, yyyy');
  }
  return version;
};

// Strip Cloudinary overlay transformations to get the base image URL
const stripCloudinaryOverlays = (url: string | null): string | null => {
  if (!url) return null;

  // Find the last occurrence of /vXXXXXXXXXX/ (version) which marks the start of the actual image path
  const versionMatch = url.match(/\/(v\d+\/[^?]+)/);
  if (!versionMatch) return url;

  // Extract the base URL and the image path
  const baseUrlMatch = url.match(/(https:\/\/[^\/]+\/[^\/]+\/image\/upload\/)/);
  if (!baseUrlMatch) return url;

  // Reconstruct with basic transformations only (auto quality and format)
  return `${baseUrlMatch[1]}q_auto,f_auto/${versionMatch[1]}`;
};

export default function DiffEditorV3() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();

  // Navigation list from Redux (ordered diff IDs from list view)
  const navigationList = useSelector((state: RootState) => state.diffs.navigationList);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [diffData, setDiffData] = useState<DiffData | null>(null);
  const [editedHook, setEditedHook] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedEvents, setEditedEvents] = useState<Record<string, { description: string; annotation_coordinates: AnnotationCoordinates | null; tags: string[] }>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isAIRewriting, setIsAIRewriting] = useState(false);

  // Inline editing state
  const [isEditingHook, setIsEditingHook] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);

  // Tag picker state
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');

  // Unsaved changes dialog state
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Navigation helpers
  const currentIndex = navigationList && id ? navigationList.indexOf(id) : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = navigationList ? currentIndex < navigationList.length - 1 && currentIndex !== -1 : false;
  const prevId = hasPrev && navigationList ? navigationList[currentIndex - 1] : null;
  const nextId = hasNext && navigationList ? navigationList[currentIndex + 1] : null;

  // Fetch diff data
  useEffect(() => {
    const fetchDiffData = async () => {
      if (!id) return;

      setLoading(true);
      try {
        // First, get the news item to extract slug and v_after
        const { data: newsItem, error: newsError } = await supabase
          .from('app_news')
          .select('slug, v_after, v_before')
          .eq('id', id)
          .single();

        if (newsError) throw newsError;
        if (!newsItem) {
          toast({
            title: "Error",
            description: "News item not found",
            variant: "destructive",
          });
          navigate('/diffs');
          return;
        }

        // Fetch all events for this slug and v_after (matching website behavior)
        // This gets ALL events regardless of v_before, fixing the missing events issue
        const { data, error } = await supabase
          .from('app_news_with_events')
          .select('*')
          .eq('slug', newsItem.slug)
          .eq('v_after', newsItem.v_after)
          .order('created_at', { ascending: true });

        if (error) throw error;

        if (!data || data.length === 0) {
          toast({
            title: "Error",
            description: "Diff not found",
            variant: "destructive",
          });
          navigate('/diffs');
          return;
        }

        // Extract diff-level data from first row
        const firstRow = data[0];
        const events: DiffEvent[] = data.map(row => ({
          id: row.id,
          event_type: row.event_type,
          category: row.category || 'misc',
          description: row.description || '',
          slug: row.slug,
          v_before: row.v_before,
          v_after: row.v_after,
          verified: row.verified,
          invalid: row.invalid,
          partially_correct: row.partially_correct,
          asset_id: row.asset_id,
          labels: Array.isArray(row.labels) ? row.labels : [],
          tags: Array.isArray(row.tags) ? row.tags : [],
          annotation_coordinates: row.annotation_coordinates,
          primary_cropped_url: row.primary_cropped_url,
          secondary_cropped_url: row.secondary_cropped_url,
          primary_original_url: row.primary_original_url,
          secondary_original_url: row.secondary_original_url,
          created_at: row.created_at,
        }));

        const diff: DiffData = {
          news_id: firstRow.news_id,
          slug: firstRow.slug,
          v_before: firstRow.v_before,
          v_after: firstRow.v_after,
          hook: firstRow.hook || '',
          news_description: firstRow.news_description || '',
          published: firstRow.published,
          period: firstRow.period,
          delta: firstRow.delta,
          logo_url: firstRow.logo_url,
          page_id: firstRow.page_id,
          events,
        };

        setDiffData(diff);
        setEditedHook(diff.hook);
        setEditedDescription(diff.news_description);

        // Initialize edited events state
        const eventsState: Record<string, { description: string; annotation_coordinates: AnnotationCoordinates | null; tags: string[] }> = {};
        events.forEach(event => {
          eventsState[event.id] = {
            description: event.description,
            annotation_coordinates: event.annotation_coordinates,
            tags: event.tags,
          };
        });
        setEditedEvents(eventsState);

      } catch (err: any) {
        console.error('Error fetching diff:', err);
        toast({
          title: "Error",
          description: err.message || "Failed to load diff data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDiffData();
  }, [id, navigate, toast]);

  // Track changes
  useEffect(() => {
    if (!diffData) return;

    const hookChanged = editedHook !== diffData.hook;
    const descChanged = editedDescription !== diffData.news_description;
    const eventsChanged = diffData.events.some(event => {
      const edited = editedEvents[event.id];
      if (!edited) return false;
      return edited.description !== event.description ||
        JSON.stringify(edited.annotation_coordinates) !== JSON.stringify(event.annotation_coordinates) ||
        JSON.stringify(edited.tags) !== JSON.stringify(event.tags);
    });

    setHasChanges(hookChanged || descChanged || eventsChanged);
  }, [editedHook, editedDescription, editedEvents, diffData]);

  // Handle event description change
  const handleEventDescriptionChange = useCallback((eventId: string, description: string) => {
    setEditedEvents(prev => ({
      ...prev,
      [eventId]: {
        ...prev[eventId],
        description,
      },
    }));
  }, []);

  // Handle annotation coordinates change
  const handleAnnotationChange = useCallback((eventId: string, coordinates: AnnotationCoordinates) => {
    setEditedEvents(prev => ({
      ...prev,
      [eventId]: {
        ...prev[eventId],
        annotation_coordinates: coordinates,
      },
    }));
  }, []);

  // Handle tag removal
  const handleRemoveTag = useCallback((eventId: string, tagToRemove: string) => {
    setEditedEvents(prev => ({
      ...prev,
      [eventId]: {
        ...prev[eventId],
        tags: prev[eventId].tags.filter(tag => tag !== tagToRemove),
      },
    }));
  }, []);

  // Handle adding a tag to all events
  const handleAddTag = useCallback((tag: string) => {
    setEditedEvents(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(eventId => {
        if (!updated[eventId].tags.includes(tag)) {
          updated[eventId] = {
            ...updated[eventId],
            tags: [...updated[eventId].tags, tag],
          };
        }
      });
      return updated;
    });
    setShowTagPicker(false);
    setTagSearchQuery('');
  }, []);

  // Track AI generation state per event
  const [aiGeneratingEventId, setAiGeneratingEventId] = useState<string | null>(null);

  // Generate description from annotation labels using AI to summarize
  const generateDescriptionFromAnnotations = useCallback(async (event: DiffEvent) => {
    const annotations = editedEvents[event.id]?.annotation_coordinates?.annotations
      || event.annotation_coordinates?.annotations
      || [];

    if (annotations.length === 0) {
      toast({
        title: "No annotations",
        description: "This event has no annotations to summarize",
        variant: "destructive",
      });
      return;
    }

    setAiGeneratingEventId(event.id);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      // Collect annotation labels sorted by number
      const changes = annotations
        .sort((a, b) => a.num - b.num)
        .map(a => ({ label: a.label, num: a.num }));

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-writer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            field: 'category_summary',
            category: event.category,
            changes,
            slug: diffData?.slug,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'AI request failed');
      }

      const data = await response.json();
      handleEventDescriptionChange(event.id, data.text);

      toast({
        title: "AI Generated",
        description: "Description generated from annotations. Click Save to persist.",
      });
    } catch (error: any) {
      console.error('AI generation error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate description with AI",
        variant: "destructive",
      });
    } finally {
      setAiGeneratingEventId(null);
    }
  }, [editedEvents, handleEventDescriptionChange, diffData, toast]);

  // Save all changes
  const handleSave = async () => {
    if (!diffData) return;

    setSaving(true);
    try {
      // Update news record (hook, description)
      const { error: newsError } = await supabase
        .from('app_news')
        .update({
          hook: editedHook,
          description: editedDescription,
        })
        .eq('id', diffData.news_id);

      if (newsError) throw newsError;

      // Update each event
      for (const event of diffData.events) {
        const edited = editedEvents[event.id];
        if (!edited) continue;

        const { error: eventError } = await supabase
          .from('app_events')
          .update({
            description: edited.description,
            annotation_coordinates: edited.annotation_coordinates,
            tags: edited.tags,
          })
          .eq('id', event.id);

        if (eventError) {
          console.error(`Error updating event ${event.id}:`, eventError);
        }
      }

      // Update local state to reflect saved values
      setDiffData(prev => prev ? {
        ...prev,
        hook: editedHook,
        news_description: editedDescription,
        events: prev.events.map(e => ({
          ...e,
          description: editedEvents[e.id]?.description || e.description,
          annotation_coordinates: editedEvents[e.id]?.annotation_coordinates || e.annotation_coordinates,
          tags: editedEvents[e.id]?.tags || e.tags,
        })),
      } : null);

      setHasChanges(false);
      toast({
        title: "Success",
        description: "Changes saved successfully",
      });
      // Clear diffs cache so fresh data loads on return (preserves navigation list)
      dispatch(clearDiffsCache());
      // Stay on page - user can navigate with prev/next or back button
    } catch (err: any) {
      console.error('Error saving:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to save changes",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Navigation with unsaved changes check
  const handleNavigate = (targetId: string | null) => {
    if (!targetId) return;

    if (hasChanges) {
      setPendingNavigation(targetId);
      setShowUnsavedDialog(true);
    } else {
      navigate(`/diffs/${targetId}/edit`);
    }
  };

  const handleBackToList = () => {
    if (hasChanges) {
      setPendingNavigation('list');
      setShowUnsavedDialog(true);
    } else {
      navigate('/diffs');
    }
  };

  const handleDiscardAndNavigate = () => {
    setShowUnsavedDialog(false);
    if (pendingNavigation === 'list') {
      navigate('/diffs');
    } else if (pendingNavigation) {
      navigate(`/diffs/${pendingNavigation}/edit`);
    }
    setPendingNavigation(null);
  };

  const handleCancelNavigation = () => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  };

  // AI Regenerate hook and description based on visible (annotated) events only
  const handleAIRegenerate = async () => {
    if (!diffData || isAIRewriting) return;

    // Get only annotated events (visible ones)
    const visibleEvents = diffData.events.filter(e =>
      e.annotation_coordinates &&
      e.annotation_coordinates.annotations &&
      e.annotation_coordinates.annotations.length > 0
    );

    if (visibleEvents.length === 0) {
      toast({
        title: "No visible events",
        description: "There are no annotated events to generate from",
        variant: "destructive",
      });
      return;
    }

    setIsAIRewriting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      // Build events summary from visible events only
      const eventsSummary = visibleEvents
        .map(e => `- [${e.category}] ${editedEvents[e.id]?.description || e.description}`)
        .join('\n');

      // Call AI for hook
      const hookResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-writer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            field: 'hook',
            context: eventsSummary || 'No events recorded',
            slug: diffData.slug,
          }),
        }
      );

      if (!hookResponse.ok) {
        const errorData = await hookResponse.json();
        throw new Error(errorData.error || 'Hook AI request failed');
      }
      const hookData = await hookResponse.json();

      // Call AI for description
      const descResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-writer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            field: 'description',
            context: eventsSummary || 'No events recorded',
            slug: diffData.slug,
          }),
        }
      );

      if (!descResponse.ok) {
        const errorData = await descResponse.json();
        throw new Error(errorData.error || 'Description AI request failed');
      }
      const descData = await descResponse.json();

      // Update local state (don't save to DB yet - user needs to click Save)
      setEditedHook(hookData.text);
      setEditedDescription(descData.text);

      toast({
        title: "AI Generated",
        description: `Hook and description regenerated from ${visibleEvents.length} visible event${visibleEvents.length !== 1 ? 's' : ''}. Click Save to persist.`,
      });
    } catch (error: any) {
      console.error('AI rewrite error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to regenerate with AI",
        variant: "destructive",
      });
    } finally {
      setIsAIRewriting(false);
    }
  };

  // Filter and group events - check edited state, not original data
  // Events with annotation_coordinates object (even if empty) are "ready to annotate"
  const annotatedEvents = diffData?.events.filter(e => {
    const editedCoords = editedEvents[e.id]?.annotation_coordinates;
    return editedCoords && editedCoords.annotations !== undefined;
  }) || [];

  const unannotatedEvents = diffData?.events.filter(e => {
    const editedCoords = editedEvents[e.id]?.annotation_coordinates;
    return !editedCoords || editedCoords.annotations === undefined;
  }) || [];

  // Sort by category
  const sortedAnnotatedEvents = [...annotatedEvents].sort((a, b) => {
    const aPriority = categoryPriority[a.category] ?? 999;
    const bPriority = categoryPriority[b.category] ?? 999;
    return aPriority - bPriority;
  });

  // Group by category
  const eventsByCategory = sortedAnnotatedEvents.reduce((acc, event) => {
    const cat = event.category || 'misc';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(event);
    return acc;
  }, {} as Record<string, DiffEvent[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!diffData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Diff not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Do you want to discard them and continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelNavigation}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscardAndNavigate} className="bg-red-600 hover:bg-red-700">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToList}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>

              <div className="flex items-center gap-3">
                {diffData.logo_url && (
                  <img
                    src={diffData.logo_url}
                    alt={diffData.slug}
                    className="h-8 w-8 rounded"
                  />
                )}
                <div>
                  <h1 className="text-lg font-semibold">{diffData.slug}</h1>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>{formatVersionDate(diffData.v_before)}</span>
                    <span>→</span>
                    <span>{formatVersionDate(diffData.v_after)}</span>
                    {diffData.period && (
                      <Badge variant="outline" className="ml-2">{diffData.period}</Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {hasChanges && (
                <span className="text-sm text-amber-600">Unsaved changes</span>
              )}
              <Button
                variant="outline"
                size="sm"
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                onClick={() => {
                  const afterDate = diffData.v_after?.match(/(\d{8})/)?.[1] || diffData.v_after;
                  const beforeDate = diffData.v_before?.match(/(\d{8})/)?.[1] || diffData.v_before;
                  const url = `https://pricingsaas.com/news/${diffData.slug}/${afterDate}/?v_before=${beforeDate}&v3=true`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
                title="Preview on PricingSaaS"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Preview
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save
              </Button>

              {/* Prev/Next navigation */}
              {navigationList && navigationList.length > 1 && (
                <div className="flex items-center gap-1 border-l pl-3 ml-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleNavigate(prevId)}
                    disabled={!hasPrev}
                    className="h-8 w-8 p-0"
                    title="Previous diff"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-gray-500 min-w-[60px] text-center">
                    {currentIndex + 1} / {navigationList.length}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleNavigate(nextId)}
                    disabled={!hasNext}
                    className="h-8 w-8 p-0"
                    title="Next diff"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Diff-level editing - centered readonly display with click to edit */}
        <Card>
          <CardContent className="py-8 relative">
            {/* AI Regenerate button in top right */}
            <div className="absolute top-3 right-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAIRegenerate}
                disabled={isAIRewriting || annotatedEvents.length === 0}
                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-8"
                title={annotatedEvents.length === 0 ? "No visible events to generate from" : `Regenerate from ${annotatedEvents.length} visible event${annotatedEvents.length !== 1 ? 's' : ''}`}
              >
                {isAIRewriting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </Button>
            </div>

            <div className="space-y-8 px-4">
              {/* Hook - Large, bold, full width */}
              <div className="relative group text-center">
                <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                  Hook
                </span>
                {isEditingHook ? (
                  <div className="mt-1">
                    <Textarea
                      value={editedHook}
                      onChange={(e) => setEditedHook(e.target.value)}
                      onBlur={() => setIsEditingHook(false)}
                      placeholder="Enter headline..."
                      rows={2}
                      maxLength={500}
                      autoFocus
                      className="text-center text-xl font-bold resize-none border-blue-300 focus:border-blue-500"
                    />
                    <div className="flex items-center justify-center gap-2 mt-1">
                      <span className="text-xs text-gray-400">{editedHook.length}/500</span>
                      {editedHook !== diffData?.hook && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevent blur before undo
                            setEditedHook(diffData?.hook || '');
                          }}
                          className="h-5 px-1.5 text-xs text-gray-500 hover:text-gray-700"
                          title="Undo changes"
                        >
                          <Undo2 className="h-3 w-3 mr-1" />
                          Undo
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="mt-1 cursor-pointer group/hook relative py-2"
                    onClick={() => setIsEditingHook(true)}
                  >
                    <p className="text-xl font-bold text-gray-900 leading-relaxed">
                      {editedHook || <span className="text-gray-300 italic">Click to add hook...</span>}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 opacity-0 group-hover/hook:opacity-100 transition-opacity absolute top-1/2 -translate-y-1/2 -right-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditingHook(true);
                      }}
                      title="Edit hook"
                    >
                      <Edit3 className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Description - Medium size, centered */}
              <div className="relative group text-center">
                <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                  Description
                </span>
                {isEditingDescription ? (
                  <div className="mt-1">
                    <Textarea
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      onBlur={() => setIsEditingDescription(false)}
                      placeholder="Enter description..."
                      rows={3}
                      maxLength={500}
                      autoFocus
                      className="text-center text-base resize-none border-blue-300 focus:border-blue-500"
                    />
                    <div className="flex items-center justify-center gap-2 mt-1">
                      <span className="text-xs text-gray-400">{editedDescription.length}/500</span>
                      {editedDescription !== diffData?.news_description && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevent blur before undo
                            setEditedDescription(diffData?.news_description || '');
                          }}
                          className="h-5 px-1.5 text-xs text-gray-500 hover:text-gray-700"
                          title="Undo changes"
                        >
                          <Undo2 className="h-3 w-3 mr-1" />
                          Undo
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="mt-1 cursor-pointer group/desc relative py-2"
                    onClick={() => setIsEditingDescription(true)}
                  >
                    <p className="text-base text-gray-600 leading-relaxed">
                      {editedDescription || <span className="text-gray-300 italic">Click to add description...</span>}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 opacity-0 group-hover/desc:opacity-100 transition-opacity absolute top-1/2 -translate-y-1/2 -right-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditingDescription(true);
                      }}
                      title="Edit description"
                    >
                      <Edit3 className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tags Section */}
        {(() => {
          // Collect all unique tags from all events
          const allTags = new Map<string, string[]>(); // tag -> eventIds that have it
          diffData.events.forEach(event => {
            const currentTags = editedEvents[event.id]?.tags || event.tags || [];
            currentTags.forEach(tag => {
              if (!allTags.has(tag)) {
                allTags.set(tag, []);
              }
              allTags.get(tag)!.push(event.id);
            });
          });

          // Get set of applied tags for filtering the picker
          const appliedTagsSet = new Set(allTags.keys());

          return (
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Tags</span>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {Array.from(allTags.entries()).map(([tag, eventIds]) => (
                    <div
                      key={tag}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-700 text-sm rounded-sm group"
                    >
                      <span>{tag.replace(/_/g, ' ')}</span>
                      <button
                        onClick={() => {
                          // Remove tag from all events that have it
                          eventIds.forEach(eventId => handleRemoveTag(eventId, tag));
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-200 rounded-sm"
                        title="Remove tag"
                      >
                        <X className="h-3 w-3 text-gray-500" />
                      </button>
                    </div>
                  ))}
                  {/* Add Tag Button */}
                  <div className="relative">
                    <button
                      onClick={() => setShowTagPicker(!showTagPicker)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-sm border border-dashed border-gray-300 hover:border-gray-400 transition-colors"
                    >
                      <Plus className="h-3 w-3" />
                      Add
                    </button>
                    {/* Tag Picker Dropdown */}
                    {showTagPicker && (
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                        {/* Search Input */}
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input
                              type="text"
                              value={tagSearchQuery}
                              onChange={(e) => setTagSearchQuery(e.target.value)}
                              placeholder="Search tags..."
                              className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400"
                              autoFocus
                            />
                          </div>
                        </div>
                        {/* Grouped Tags */}
                        <div className="max-h-72 overflow-y-auto p-1">
                          {Object.entries(TAG_CATEGORIES).map(([category, tags]) => {
                            const availableTags = tags
                              .filter(tag => !appliedTagsSet.has(tag))
                              .filter(tag => !tagSearchQuery || tag.toLowerCase().includes(tagSearchQuery.toLowerCase()));

                            if (availableTags.length === 0) return null;

                            return (
                              <div key={category} className="mb-2">
                                <div className="text-xs font-semibold text-gray-500 px-2 py-1 uppercase tracking-wide">
                                  {category}
                                </div>
                                {availableTags.map(tag => (
                                  <button
                                    key={tag}
                                    onClick={() => handleAddTag(tag)}
                                    className="w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-sm"
                                  >
                                    {tag.replace(/_/g, ' ')}
                                  </button>
                                ))}
                              </div>
                            );
                          })}
                          {/* No results message */}
                          {Object.entries(TAG_CATEGORIES).every(([_, tags]) => {
                            const availableTags = tags
                              .filter(tag => !appliedTagsSet.has(tag))
                              .filter(tag => !tagSearchQuery || tag.toLowerCase().includes(tagSearchQuery.toLowerCase()));
                            return availableTags.length === 0;
                          }) && (
                            <div className="text-sm text-gray-400 text-center py-4">
                              {tagSearchQuery ? 'No matching tags' : 'All tags applied'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Unannotated Events Section */}
        {unannotatedEvents.length > 0 && (
          <Card className="border-dashed border-2 border-amber-300 bg-amber-50/30">
            <CardHeader>
              <CardTitle className="text-amber-900 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Events Without Annotations ({unannotatedEvents.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {unannotatedEvents.map((event) => (
                <div key={event.id} className="bg-white rounded-lg p-4 border border-amber-200">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${getCategoryColors(event.category).dot}`} />
                      <h3 className={`font-semibold ${getCategoryColors(event.category).text}`}>
                        {formatCategoryLabel(event.category)}
                      </h3>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Initialize with a starter annotation and clean base Cloudinary URLs (no overlays)
                        const initialAnnotation: AnnotationCoordinates = {
                          cloudinary_before: stripCloudinaryOverlays(event.primary_original_url) || undefined,
                          cloudinary_after: stripCloudinaryOverlays(event.secondary_original_url) || undefined,
                          annotations: [{
                            num: 1,
                            label: editedEvents[event.id]?.description?.substring(0, 50) || 'Annotation 1',
                            before: { x: 500, y: 400 }, // Default center-ish position
                            after: { x: 500, y: 400 },
                          }]
                        };
                        handleAnnotationChange(event.id, initialAnnotation);
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Annotations
                    </Button>
                  </div>

                  {/* Event description - editable */}
                  {editingEventId === event.id ? (
                    <div>
                      <Textarea
                        value={editedEvents[event.id]?.description || ''}
                        onChange={(e) => handleEventDescriptionChange(event.id, e.target.value)}
                        onBlur={() => setEditingEventId(null)}
                        placeholder="Event description..."
                        rows={2}
                        maxLength={500}
                        autoFocus
                        className="text-gray-700 resize-none border-blue-300 focus:border-blue-500"
                      />
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">
                          {(editedEvents[event.id]?.description || '').length}/500
                        </span>
                        {editedEvents[event.id]?.description !== event.description && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleEventDescriptionChange(event.id, event.description);
                            }}
                            className="h-5 px-1.5 text-xs text-gray-500 hover:text-gray-700"
                            title="Undo changes"
                          >
                            <Undo2 className="h-3 w-3 mr-1" />
                            Undo
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div
                      className="group cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 pr-16 relative"
                      onClick={() => setEditingEventId(event.id)}
                    >
                      <p className="text-gray-700 leading-relaxed">
                        {editedEvents[event.id]?.description || event.description || <span className="text-gray-300 italic">Click to add description...</span>}
                      </p>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity absolute top-1/2 -translate-y-1/2 right-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingEventId(event.id);
                          }}
                          title="Edit description"
                        >
                          <Edit3 className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                        </Button>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-amber-700 mt-2">
                    This event needs annotations to be visible on the public news page. Click "Add Annotations" above to create them.
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* No annotated events message */}
        {annotatedEvents.length === 0 && unannotatedEvents.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <ImageIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No events found</p>
            </CardContent>
          </Card>
        )}

        {/* Events grouped by category */}
        {Object.entries(eventsByCategory).map(([category, events]) => {
          const colors = getCategoryColors(category);
          return (
          <Card key={category} className={`overflow-hidden border-2 ${colors.border}`}>
            <CardContent className="p-6">
              {/* Category Header */}
              <div className="flex items-center gap-2 mb-4">
                <div className={`w-3 h-3 rounded-full ${colors.dot}`} />
                <h2 className={`text-lg font-semibold ${colors.text}`}>
                  {formatCategoryLabel(category)}
                </h2>
              </div>

              {/* Events in this category */}
              <div className="space-y-6">
                {events.map((event) => (
                  <div key={event.id} className="space-y-4">
                    {/* Event description - editable on click */}
                    {editingEventId === event.id ? (
                      <div>
                        <Textarea
                          value={editedEvents[event.id]?.description || ''}
                          onChange={(e) => handleEventDescriptionChange(event.id, e.target.value)}
                          onBlur={() => setEditingEventId(null)}
                          placeholder="Event description..."
                          rows={2}
                          maxLength={500}
                          autoFocus
                          className="text-gray-700 resize-none border-blue-300 focus:border-blue-500"
                        />
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-400">
                            {(editedEvents[event.id]?.description || '').length}/500
                          </span>
                          {editedEvents[event.id]?.description !== event.description && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleEventDescriptionChange(event.id, event.description);
                              }}
                              className="h-5 px-1.5 text-xs text-gray-500 hover:text-gray-700"
                              title="Undo changes"
                            >
                              <Undo2 className="h-3 w-3 mr-1" />
                              Undo
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        className="group cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 pr-16 relative"
                        onClick={() => setEditingEventId(event.id)}
                      >
                        <p className="text-gray-700 leading-relaxed">
                          {editedEvents[event.id]?.description || event.description || <span className="text-gray-300 italic">Click to add description...</span>}
                        </p>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity absolute top-1/2 -translate-y-1/2 right-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              generateDescriptionFromAnnotations(event);
                            }}
                            disabled={aiGeneratingEventId === event.id}
                            title="AI summarize annotations"
                          >
                            {aiGeneratingEventId === event.id ? (
                              <Loader2 className="h-3.5 w-3.5 text-purple-400 animate-spin" />
                            ) : (
                              <Sparkles className="h-3.5 w-3.5 text-purple-400 hover:text-purple-600" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingEventId(event.id);
                            }}
                            title="Edit description"
                          >
                            <Edit3 className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Annotation Editor */}
                    {editedEvents[event.id]?.annotation_coordinates && (() => {
                      // Use cloudinary URLs from annotation_coordinates if available (these are base URLs without annotations)
                      // Otherwise fall back to stripped original URLs from the event
                      const coords = editedEvents[event.id].annotation_coordinates!;
                      const beforeUrl = coords.cloudinary_before || stripCloudinaryOverlays(event.primary_original_url);
                      const afterUrl = coords.cloudinary_after || stripCloudinaryOverlays(event.secondary_original_url);

                      return (
                        <AnnotationEditor
                          annotationCoordinates={coords}
                          initialAnnotationCoordinates={event.annotation_coordinates ?? undefined}
                          primaryOriginalUrl={beforeUrl}
                          secondaryOriginalUrl={afterUrl}
                          onChange={(coords) => handleAnnotationChange(event.id, coords)}
                        />
                      );
                    })()}

                    {/* Separator between events in same category */}
                    {events.indexOf(event) < events.length - 1 && (
                      <hr className="border-gray-100" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>
    </div>
  );
}
